import React from 'react';
import { PriceTag } from './PriceTag';

/**
 * FloorplanScroller — 상세페이지 평면도 가로 스크롤/그리드.
 *
 * 각 카드는 SVG/이미지 + 타입명 + 전용면적 + (가격 셀이 있다면 PriceTag).
 * 분양가는 정책상 PriceTag로 강제 → "가격 문의" 출력.
 *
 * layout (Phase 6.5):
 *   - 'scroll'     : 모바일 가로 스크롤 (기본, 모든 viewport)
 *   - 'grid-2'     : 2열 그리드
 *   - 'grid-3'     : 3열 그리드
 *   - 'grid-4'     : 4열 그리드
 *   - 'responsive' : 모바일 scroll → md:2열 → lg:3열 (시안 .r-fp-grid 매핑,
 *                    상세 페이지 main 영역 폭 ~780px 고려)
 */

export interface FloorplanItem {
  id: number | string;
  typeName: string;       // 예: "84A"
  /** 전용면적 ㎡ */
  areaExclusive?: number | null;
  /** 세대수 */
  units?: number | null;
  imageUrl?: string | null;
  /** SVG 노드 — 이미지 대신 직접 그릴 때 */
  svg?: React.ReactNode;
  /** 분양가 최고가 (만원) — PropertyTypePrice.top_price */
  topPrice?: number | null;
}

export type FloorplanLayout = 'scroll' | 'grid-2' | 'grid-3' | 'grid-4' | 'responsive';

export interface FloorplanScrollerProps {
  items: FloorplanItem[];
  /** 레이아웃 — 기본 'scroll' (모바일 회귀 보장) */
  layout?: FloorplanLayout;
  /** 가격 셀 노출 (기본 true — 항상 "가격 문의") */
  showPrice?: boolean;
  className?: string;
}

// 레이아웃별 컨테이너 / 카드 클래스
const LAYOUT_CONTAINER: Record<FloorplanLayout, string> = {
  scroll:     'flex gap-3 overflow-x-auto scrollbar-none px-4 -mx-4',
  'grid-2':   'grid grid-cols-2 gap-3',
  'grid-3':   'grid grid-cols-3 gap-3',
  'grid-4':   'grid grid-cols-4 gap-3',
  // 반응형: 모바일 scroll → md 2 → lg 3
  responsive: 'flex gap-3 overflow-x-auto scrollbar-none px-4 -mx-4 ' +
              'md:grid md:grid-cols-2 md:gap-4 md:overflow-visible md:px-0 md:mx-0 ' +
              'lg:grid-cols-3 lg:gap-5',
};

const LAYOUT_CARD: Record<FloorplanLayout, string> = {
  scroll:     'shrink-0 w-[200px]',
  'grid-2':   '',
  'grid-3':   '',
  'grid-4':   '',
  // 반응형: 모바일 카드 width 고정, grid 모드에서는 auto
  responsive: 'shrink-0 w-[200px] md:w-auto md:shrink',
};

export const FloorplanScroller: React.FC<FloorplanScrollerProps> = ({
  items,
  layout = 'scroll',
  showPrice = true,
  className = '',
}) => {
  if (items.length === 0) return null;
  return (
    <div
      className={`${LAYOUT_CONTAINER[layout]} ${className}`}
      style={layout === 'scroll' || layout === 'responsive' ? { scrollbarWidth: 'none' } : undefined}
    >
      {items.map((it) => (
        <article
          key={it.id}
          className={`${LAYOUT_CARD[layout]} rounded-ca-2xl border border-ca-line bg-ca-surface overflow-hidden`}
        >
          <div className="relative aspect-[4/3] bg-block flex items-center justify-center">
            {it.svg ? (
              it.svg
            ) : it.imageUrl ? (
              <img src={it.imageUrl} alt={it.typeName} loading="lazy" className="h-full w-full object-contain" />
            ) : (
              <span className="text-[10.5px] uppercase tracking-widest text-mute2 font-mono">no plan</span>
            )}
            {typeof it.units === 'number' && it.units > 0 && (
              <span className="absolute left-2 top-2 rounded-ca-sm bg-frame/92 px-2 py-1 text-[10.5px] font-bold text-ink backdrop-blur">
                {it.units}세대
              </span>
            )}
          </div>
          <div className="px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[14.5px] font-extrabold text-ink tracking-tight">{it.typeName}</span>
              {typeof it.areaExclusive === 'number' && it.areaExclusive > 0 && (
                <span className="text-[11.5px] font-medium text-muted tabular-nums">
                  전용 {it.areaExclusive.toFixed(1)}㎡
                </span>
              )}
            </div>
            {showPrice && (
              <div className="mt-1.5">
                <PriceTag size="sm" maxPrice={it.topPrice} />
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};

export default FloorplanScroller;
